import type { CSSProperties, ReactNode } from "react";
import RenderInSlot from "../../../ui/layout/RenderInSlot";
import type { RunMargins } from "../../threads";

/** The width of a closed column's rail; the focus pane takes the rest (web-app.md WA-1). */
const RAIL_WIDTH = "2.25rem";

interface ReviewPanesProps {
  /** The Run page's margins, where a review in a step puts its outline and context; `null` keeps them beside the focus. */
  readonly margins: RunMargins | null;
  readonly outlineOpen: boolean;
  readonly contextOpen: boolean;
  readonly outline: ReactNode;
  readonly focus: ReactNode;
  readonly context: ReactNode;
}

/**
 * The review's three panes in their places. Beside the focus in one grid; in a Run with room
 * for margins, the outline in the left margin and the context in the right, each next to the
 * step and in view while it scrolls, and the focus the column's width (Johannes, 4 Nov). The
 * panes keep their state when they move: they stay the review's, only rendered elsewhere.
 */
export default function ReviewPanes({ margins, outlineOpen, contextOpen, outline, focus, context }: ReviewPanesProps) {
  // One tree in both places, the focus always the same child of the same element: a pane resized across the margins'
  // cut-off keeps the focus mounted, with what the person is typing in it (Sol review R5, 4 Nov).
  return (
    <div
      className={margins === null ? "grid gap-4 pane-md:grid-cols-[var(--review-outline)_minmax(0,1fr)] pane-lg:grid-cols-[var(--review-outline)_minmax(0,1fr)_var(--review-context)]" : undefined}
      style={margins === null ? { "--review-outline": outlineOpen ? "17rem" : RAIL_WIDTH, "--review-context": contextOpen ? "20rem" : RAIL_WIDTH } as CSSProperties : undefined}
    >
      {margins === null ? outline : (
        <RenderInSlot slot={margins.left}>
          <div className="sticky top-2 self-start" style={{ width: outlineOpen ? "100%" : RAIL_WIDTH }}>{outline}</div>
        </RenderInSlot>
      )}
      {focus}
      {margins === null ? context : (
        <RenderInSlot slot={margins.right}>
          <div className="sticky top-2 self-start" style={{ width: contextOpen ? "100%" : RAIL_WIDTH }}>{context}</div>
        </RenderInSlot>
      )}
    </div>
  );
}
